'use client';

import type {
  Breadcrumb,
  ProductDetailData,
} from '@eliadmin/core/src/api/products/types/product-types';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { FiHome } from 'react-icons/fi';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  product?: ProductDetailData;
  breadcrumb: Breadcrumb;
};

export function Breadcrumbs({ breadcrumb, className, homeText, product }: BreadcrumbsProps) {
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <nav className={cn('w-full', 'ea-breadcrumbs', className?.wrapper)}>
      <ol
        className={cn(
          'flex flex-row flex-wrap items-center space-x-2 text-[#666666]',
          'ea-breadcrumbs__list',
          className?.list,
        )}
      >
        <li
          className={cn(
            {
              [cn(
                'pointer-events-none text-[#666666]',
                'ea_breadcrumbs__item--active',
                className?.itemActive,
              )]: arePathsEqual(pathname, getSlugFromPage('home')),
            },
            'ea_breadcrumbs__item',
            className?.item,
          )}
        >
          <Link
            href={getSlugFromPage('home') ?? '/'}
            className={cn('ea-breadcrumbs__link', className?.link)}
            prefetch={false}
          >
            {homeText ? homeText : <FiHome />}
          </Link>
        </li>
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={index} className="flex w-auto items-center justify-start space-x-2">
            <div className={cn('h-[3px] w-[3px] rounded-full bg-[#D1D1D1]')} />
            <span
              className={cn(
                {
                  [cn(
                    'pointer-events-none text-[#666666]',
                    'ea_breadcrumbs__item--active',
                    className?.itemActive,
                  )]: arePathsEqual(pathname, breadcrumb.slug),
                },
                'ea_breadcrumbs__item',
                className?.item,
              )}
            >
              <Link
                href={breadcrumb.slug ?? '#'}
                className={cn('ea-breadcrumbs__link', className?.link)}
                prefetch={false}
              >
                {breadcrumb.name}
              </Link>
            </span>
          </li>
        ))}
        {product && (
          <li className="flex w-auto items-center justify-start space-x-2">
            <div className={cn('h-[3px] w-[3px] rounded-full bg-[#D1D1D1]')} />
            <span
              className={cn(
                {
                  [cn(
                    'pointer-events-none text-[#666666]',
                    'ea_breadcrumbs__item--active',
                    className?.itemActive,
                  )]: arePathsEqual(pathname, breadcrumb.slug),
                },
                'ea_breadcrumbs__item',
                className?.item,
              )}
            >
              <Link
                href={product.slug ?? '#'}
                className={cn('ea-breadcrumbs__link', className?.link)}
                prefetch={false}
              >
                {product.name}
              </Link>
            </span>
          </li>
        )}
      </ol>
    </nav>
  );
}
